import type { QueryClient } from '@tanstack/react-query';
import { PreviewKind, PreviewSource } from './agent-link-preview.type';
import type { CachedDetails, PreviewTarget, PreviewTaskRecord } from './agent-link-preview.type';

/** Jira Cloud hosts; `*.jira.com` covers sites created before the move to atlassian.net. */
const JIRA_CLOUD_HOST_SUFFIXES = ['.atlassian.net', '.jira.com'];

/**
 * Converts a rich-text description into a single plain-text line for the card.
 * Strips HTML tags, collapses whitespace and caps the length at 360 characters.
 *
 * @param {string | undefined} value - Raw description, possibly HTML
 * @returns {string | undefined} Plain text ending in "…" when truncated, or undefined when empty
 */
function previewDescription(value: string | undefined): string | undefined {
  const text = value
    ?.replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return undefined;
  return text.length > 360 ? `${text.slice(0, 360).trimEnd()}…` : text;
}

/**
 * Returns the router path for a same-origin link into the app, so it can be
 * rendered as a client-side `<Link>` instead of a full page load.
 *
 * @param {string | undefined} href - The link's href, absolute or relative
 * @returns {string | undefined} Path plus search and hash under `/application`, or undefined for any other link
 */
function getInternalPath(href: string | undefined): string | undefined {
  if (!href) return undefined;

  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return undefined;
    if (url.pathname !== '/application' && !url.pathname.startsWith('/application/')) return undefined;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}

/**
 * Recognises links that can be previewed and parses what the card needs from them.
 *
 * Responsibilities:
 * 1. Map in-app URLs to Bloomfilter dashboards, initiatives and pages
 * 2. Match Jira issue URLs (`/browse/KEY-123`, optionally under a context path) on any
 *    host, flagging Jira Cloud and the organization's configured domain as known hosts
 * 3. Match Azure DevOps work-item URLs (`/_workitems/edit/123`) on any host, flagging
 *    `dev.azure.com` and `*.visualstudio.com` as known hosts
 *
 * @param {string | undefined} href - The link's href
 * @param {string | undefined} jiraDomain - The organization's Jira domain, with or without a protocol
 * @returns {PreviewTarget | undefined} The parsed target, or undefined for links with no preview
 */
function getPreviewTarget(href: string | undefined, jiraDomain: string | undefined): PreviewTarget | undefined {
  if (!href) return undefined;

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return undefined;
  }
  if (!['http:', 'https:'].includes(url.protocol)) return undefined;

  if (getInternalPath(href)) {
    const parts = url.pathname.split('/').filter(Boolean);
    if (parts[1] === 'dashboards' && parts.length === 3) {
      return {
        source: PreviewSource.BLOOMFILTER,
        kind: PreviewKind.DASHBOARD,
        title: 'Dashboard',
        id: parts[2],
        url,
        knownHost: true,
      };
    }
    if (parts[1] === 'strategy' && parts.length === 4 && !['goals', 'outcomes', 'epics'].includes(parts[2])) {
      return {
        source: PreviewSource.BLOOMFILTER,
        kind: PreviewKind.INITIATIVE,
        title: 'Initiative',
        portfolioId: parts[2],
        id: parts[3],
        url,
        knownHost: true,
      };
    }
    if (parts[1] === 'flora-agent' && parts[2] === 'pages' && parts[3]) {
      return {
        source: PreviewSource.BLOOMFILTER,
        kind: PreviewKind.PAGE,
        title: 'Flora page',
        id: parts[3],
        url,
        knownHost: true,
      };
    }
    return {
      source: PreviewSource.BLOOMFILTER,
      kind: PreviewKind.PAGE,
      title: 'Bloomfilter page',
      url,
      knownHost: true,
    };
  }

  const configuredHost = jiraDomain
    ? (() => {
        try {
          return new URL(jiraDomain.includes('://') ? jiraDomain : `https://${jiraDomain}`).hostname;
        } catch {
          return undefined;
        }
      })()
    : undefined;
  const host = url.hostname.toLowerCase();

  // Jira Server/Data Center can live under a context path, e.g. /jira/browse/KEY-1.
  const issueKey = url.pathname.match(/^(?:\/[^/]+)*\/browse\/([A-Z][A-Z0-9_]+-\d+)\/?$/i)?.[1];
  if (issueKey) {
    const key = issueKey.toUpperCase();
    return {
      source: PreviewSource.JIRA,
      kind: PreviewKind.ISSUE,
      title: key,
      id: key,
      url,
      knownHost: JIRA_CLOUD_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix)) || host === configuredHost,
    };
  }

  // Azure DevOps Server uses /<collection>/<project>/_workitems/edit/<id> on any host.
  const workItemId = url.pathname.match(/\/_workitems\/edit\/(\d+)\/?$/i)?.[1];
  if (workItemId) {
    return {
      source: PreviewSource.AZURE_DEVOPS,
      kind: PreviewKind.WORK_ITEM,
      title: `Work item ${workItemId}`,
      id: workItemId,
      url,
      knownHost: host === 'dev.azure.com' || host.endsWith('.visualstudio.com'),
    };
  }
  return undefined;
}

/**
 * Builds up to two uppercase initials for an assignee avatar.
 *
 * @param {string} name - A display name, e.g. "Priya Raman"
 * @returns {string} Initials such as "PR"; empty when the name has no letters
 */
function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
}

/**
 * Checks whether a task URL points at the same item as the previewed link,
 * ignoring query strings, hashes and trailing slashes.
 *
 * @param {string} first - A task URL from an API payload
 * @param {URL} second - The previewed link's URL
 * @returns {boolean} True when origin and path match; false when `first` is not a valid URL
 */
function sameDestination(first: string, second: URL): boolean {
  try {
    const url = new URL(first);
    return url.origin === second.origin && url.pathname.replace(/\/$/, '') === second.pathname.replace(/\/$/, '');
  } catch {
    return false;
  }
}

/**
 * Returns a task name worth showing as the card title. Names that only repeat
 * the issue key are dropped, since the key is already in the header.
 *
 * @param {string | undefined} name - The task's name
 * @param {PreviewTarget} target - The previewed link, whose `id` is the key
 * @returns {string | undefined} The trimmed name, or undefined when empty or equal to the key
 */
function taskDisplayName(name: string | undefined, target: PreviewTarget): string | undefined {
  const value = name?.trim();
  return value && value.toUpperCase() !== target.id?.toUpperCase() ? value : undefined;
}

/**
 * Maps a task record into preview details. Task payloads differ by endpoint —
 * `epic` is an `{ name, title }` object from task search but a plain name
 * elsewhere — so every field is read defensively.
 *
 * @param {PreviewTaskRecord} task - A task from the search endpoint or a cached task list
 * @param {PreviewTarget} target - The link being previewed, used to drop names that just repeat the key
 * @returns {CachedDetails} Display-ready details; absent fields are undefined
 */
function taskDetails(task: PreviewTaskRecord, target: PreviewTarget): CachedDetails {
  const epic =
    task.epic && typeof task.epic === 'object'
      ? (task.epic as { name?: unknown; title?: unknown })
      : { name: task.epic, title: task.epic_key };
  const epicName = typeof epic.name === 'string' && epic.name.trim() ? epic.name.trim() : undefined;
  const epicKey = typeof epic.title === 'string' && epic.title.trim() ? epic.title.trim() : undefined;
  return {
    title: taskDisplayName(task.name, target),
    description: previewDescription(task.description),
    type: task.raw_type_label || task.type,
    assignee: task.assignee_name || undefined,
    assigneeKnown: 'assignee_name' in task,
    status: task.current_section?.trim() || undefined,
    epicKey: epicName && epicKey && epicKey !== epicName ? epicKey : undefined,
    epicName: epicName ?? epicKey,
    matched: true,
  };
}

/**
 * Reads preview details from data already in the React Query cache, so the
 * card can show something before (or without) a network lookup.
 *
 * Responsibilities:
 * 1. For initiatives, read the single-initiative query, then the portfolio's initiative list
 * 2. For dashboards, scan cached `pages` lists for the current organization
 * 3. For Jira/ADO, scan cached task search results for the current organization and
 *    initiative task lists for the current portfolio
 *
 * Only cache entries whose key names the current organization or portfolio are
 * read. The cache survives organization switches, so an unscoped entry (such as
 * epic, goal or outcome task lists) could otherwise surface another
 * organization's item while the lookup is pending or after it fails.
 *
 * @param {QueryClient} queryClient - The app's query client
 * @param {PreviewTarget} target - The previewed link
 * @param {string | undefined} organizationId - The current organization; without it no org-scoped entry is read
 * @param {string | undefined} portfolioId - The current portfolio, used to scope initiative task lists
 * @returns {CachedDetails} Details from the first matching cache entry, or an empty object
 */
function cachedDetails(
  queryClient: QueryClient,
  target: PreviewTarget,
  organizationId: string | undefined,
  portfolioId: string | undefined,
): CachedDetails {
  if (target.source === PreviewSource.BLOOMFILTER) {
    if (target.kind === PreviewKind.INITIATIVE && target.portfolioId && target.id) {
      const initiative = queryClient.getQueryData<{ name?: string; description?: string }>([
        'initiative',
        target.portfolioId,
        target.id,
      ]);
      if (initiative?.name)
        return { title: initiative.name, description: previewDescription(initiative.description), matched: true };
      const initiatives = queryClient.getQueryData<{ id: string; name: string; description?: string }[]>([
        'initiatives',
        target.portfolioId,
      ]);
      const match = initiatives?.find((item) => item.id === target.id);
      return match ? { title: match.name, description: previewDescription(match.description), matched: true } : {};
    }
    if (target.kind === PreviewKind.DASHBOARD && target.id && organizationId) {
      // Keys are ['pages', organizationId, portfolioId, type, moduleType].
      for (const [, data] of queryClient.getQueriesData<{ id?: string; name: string; description?: string }[]>({
        queryKey: ['pages', organizationId],
      })) {
        const page = Array.isArray(data) ? data.find((item) => item.id === target.id) : undefined;
        if (page) return { title: page.name, description: previewDescription(page.description), matched: true };
      }
    }
    return {};
  }

  // Keys are ['task-search', query, organizationId, …] and
  // ['initiative-tasks', portfolioId, initiativeId, …].
  const scopedTaskCaches = [
    ...(organizationId ? [{ prefix: 'task-search', scopeIndex: 2, scope: organizationId }] : []),
    ...(portfolioId ? [{ prefix: 'initiative-tasks', scopeIndex: 1, scope: portfolioId }] : []),
  ];
  for (const { prefix, scopeIndex, scope } of scopedTaskCaches) {
    for (const [queryKey, data] of queryClient.getQueriesData<unknown>({ queryKey: [prefix] })) {
      if (queryKey[scopeIndex] !== scope) continue;
      if (!Array.isArray(data)) continue;
      const tasks = data.flatMap((entry) =>
        entry && typeof entry === 'object' && Array.isArray(entry.tasks) ? entry.tasks : [entry],
      );
      const task = tasks.find(
        (entry) => entry && typeof entry.url === 'string' && sameDestination(entry.url, target.url),
      );
      if (task) return taskDetails(task as PreviewTaskRecord, target);
    }
  }
  return {};
}

export {
  cachedDetails,
  getInternalPath,
  getPreviewTarget,
  initialsFor,
  previewDescription,
  sameDestination,
  taskDetails,
  taskDisplayName,
};
